import {
  boxShadow,
  mediaHelper,
  P,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import { css, styled } from 'styled-components';
import { SubBrickTitle } from './SubBrickTitle';

const Title = styled(SubBrickTitle)`
  margin-bottom: ${spacingHelper('extraSmall')};
`;

const Tile = styled.div`
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  border-radius: ${radiusHelper('medium')};

  padding: 1.25rem ${spacingHelper('medium')} ${spacingHelper('medium')};

  ${mediaHelper('tabletMin')} {
    padding: 1.25rem ${spacingHelper('large')} ${spacingHelper('large')};
  }

  display: flex;
  flex-direction: column;
`;

const Value = styled.span`
  ${typographyHelper('title03')};
`;

const Unit = styled.span`
  ${typographyHelper('caption')}
`;

const AdditionalInfoValue = styled.span<{ variant: 'GREEN' | 'BLACK' }>`
  ${typographyHelper('subtitle03')}
  font-variation-settings: "XTDR" 50, "APRT" 100, "SPAC" 40;
  ${({ variant }) => {
    switch (variant) {
      case 'GREEN':
        return css`
          color: ${paletteHelper('textGreen')};
        `;
      case 'BLACK':
      default:
        return '';
    }
  }}
`;

const Suffix = styled.span`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

export const HorizontalLine = styled.div<{ variant: LineVariant }>`
  width: 100%;
  height: 2px;
  border-radius: ${radiusHelper('medium')};
  margin: ${spacingHelper('extraSmall')} 0 ${spacingHelper('extraSmall')} 0;
  ${({ variant }) => {
    switch (variant) {
      case 'GREEN':
        return css`
          background-color: ${paletteHelper('backgroundGreen')};
        `;
      case 'GREY':
        return css`
          background-color: ${paletteHelper('backgroundSecondary')};
        `;
      default:
        return '';
    }
  }}
`;

export type LineVariant = 'GREEN' | 'GREY';

interface DemographicsTile {
  title: string;
  valueWithUnit: {
    value: string;
    unit: string;
  };
  additionalInfo?: {
    value: string;
    suffix: string;
    variant: 'GREEN' | 'BLACK';
    lineVariant: LineVariant;
  } | null;
  className?: string;
}

export const DemographicsTile: FunctionComponent<DemographicsTile> = ({
  title,
  valueWithUnit: { value, unit },
  additionalInfo,
  className: className,
}) => {
  return (
    <Tile className={className}>
      <Title as="h3">{title}</Title>
      <P>
        <Value>{value}</Value> <Unit>{unit}</Unit>
      </P>
      {additionalInfo && (
        <>
          <HorizontalLine variant={additionalInfo.lineVariant} />
          <P>
            <AdditionalInfoValue variant={additionalInfo.variant}>
              {additionalInfo.value}
            </AdditionalInfoValue>{' '}
            <Suffix>{additionalInfo.suffix}</Suffix>
          </P>
        </>
      )}
    </Tile>
  );
};
